import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { durationFrames, textLine, type Cue, type QCIssue } from '../../core';
import { nextVisibleCol } from './gridnav';
import { rowFlagLabel, summarizeRowIssues, type FlagColumn } from './rowIssues';
import {
  cpsThreshold,
  cueCps,
  durationMs,
  durationThreshold,
  durationTitle,
  formatTimecode,
  parseTimecode,
} from './metrics';
import { scrollTopForRow, virtualWindow } from './virtual';
import './CueTable.css';

// Tall enough for a typical two-line subtitle cue (13px text, 1.35 line-height)
// plus vertical padding; longer cues clamp (CueTable.css .cf-cell-text) so they
// never overflow the fixed-height virtualized row.
const ROW_HEIGHT = 48;
const DEFAULT_VIEWPORT = 600; // fallback when layout is unmeasured (e.g. jsdom)
const TIMECODE_ERROR = 'Use HH:MM:SS,mmm or HH:MM:SS.mmm';

type TimingField = 'start' | 'end';
type EditField = TimingField | 'text';
interface Editing {
  cue: Cue;
  field: EditField;
}

interface CellPos {
  row: number;
  col: number;
}

// Column order: index | start | end | dur | cps | text | (actions)
const COL: Record<EditField | 'index' | 'dur' | 'cps' | 'actions', number> = {
  index: 0,
  start: 1,
  end: 2,
  dur: 3,
  cps: 4,
  text: 5,
  actions: 6,
};

interface CueTableProps {
  cues: Cue[];
  /**
   * The document's unfiltered cue count (cue-forge-wwy.2). `cues` may be a
   * find-filtered subset (cue-forge-q51), so `cues.length === 0` alone can't
   * tell "the document has no cues at all" (show the empty-table affordance)
   * apart from "the filter matched nothing" (must NOT show it). Defaults to
   * `cues.length` for callers with no filter concept, which makes the two cases
   * coincide.
   */
  totalCueCount?: number;
  /** Called with the fully-updated cue when an inline edit is committed. */
  onCommitCue: (cue: Cue) => void;
  /** Merge the cue at the given array position with the next; enables actions. */
  onMergeNext?: (pos: number) => void;
  /** Split the cue at the given array position; enables actions. */
  onSplit?: (pos: number) => void;
  /** Insert a new empty cue after the given array position; enables actions. */
  onInsertAfter?: (pos: number) => void;
  /** Delete the cue at the given array position; enables actions. */
  onDelete?: (pos: number) => void;
  /**
   * When set the Merge/Split/Insert/Delete buttons show but are disabled with an
   * explanatory title (cue-forge-drt.15) — used while a find-filter is active,
   * where position-based structural edits would be ambiguous. Vanishing buttons
   * read as a regression, not a rule; mirrors {@link advancedEditDisabled}.
   */
  mergeSplitDisabled?: boolean;
  /**
   * Open the Advanced edit dialog for a cue (cue-forge-b0w). Enables actions.
   * When {@link advancedEditDisabled} is set the button shows but is disabled —
   * used when the source format carries no inline styling (e.g. SBV).
   */
  onAdvancedEdit?: (cue: Cue) => void;
  advancedEditDisabled?: boolean;
  /**
   * Code-point ranges excluded from the CPS display, mirroring the QC
   * `max-cps` rule's `excludeRanges` (e.g. Arabic harakat in netflix-arabic).
   */
  cpsExcludeRanges?: [number, number][];
  /**
   * Active preset thresholds (cue-forge-n3e.4): the CPS and Duration cells tint
   * amber near a limit and red past it, so a long file can be scanned. Optional
   * so the table degrades gracefully when no QC preset is available.
   */
  maxCps?: number;
  minDurationMs?: number;
  maxDurationMs?: number;
  /**
   * Document frame rate (cue-forge-d9b.1): when known, the duration cell renders
   * a frame count (`50 fr`) instead of seconds, matching broadcast/Netflix QC,
   * with the seconds value kept in the cell title. Undefined → seconds, as before.
   */
  frameRate?: number;
  /**
   * Live QC issues (cue-forge-n3e.5): each flagged row gets a severity marker in
   * the index gutter and a tinted offending cell, so the operator can see which
   * cues are flagged while editing in the grid instead of mapping '#N' from the
   * Inspector. Empty/undefined until QC has been run.
   */
  issues?: QCIssue[];
  /**
   * Issues the operator accepted as intentional (cue-forge-n3e.11). The n3e.4
   * CPS/duration cell tints are metric-driven, so they stay lit after an issue
   * is accepted; this lets the table clear the tint on the accepted metric's
   * cell (max-cps → CPS cell, min/max-duration → duration cell) so an accepted
   * cue stops reading as "still wrong".
   */
  accepted?: QCIssue[];
  /**
   * 1-based cue index to bring into view; when this changes to a non-null
   * value the table scrolls the row to the top and focuses its text cell.
   */
  scrollToIndex?: number | null;
  /** 0-based array position of the selected row; highlighted when set. */
  selectedIndex?: number | null;
  /**
   * 0-based array position of the cue under the playhead ("now playing"); gets a
   * subtle highlight, independent of selection (no aria-selected).
   */
  activeIndex?: number | null;
  /** Called with the row's 0-based array position when its row is clicked. */
  onSelectCue?: (i: number | null) => void;
}

/**
 * Virtualized, inline-editable cue table (spec §4.1 #3, §8). Columns: index,
 * start, end, duration, CPS, text. Only the visible row window is mounted so the
 * table stays responsive at 10k cues. Timecodes are monospace and LTR; text
 * cells use dir="auto" per line so Arabic renders right-to-left beside them.
 * Keyboard support follows the ARIA grid pattern: a single roving tabindex,
 * arrow-key navigation (which scrolls the virtual window as needed), and
 * Enter/Space/F2 to open the inline editor on editable cells.
 */
export function CueTable({
  cues,
  totalCueCount,
  onCommitCue,
  onMergeNext,
  onSplit,
  onInsertAfter,
  onDelete,
  mergeSplitDisabled,
  onAdvancedEdit,
  advancedEditDisabled,
  cpsExcludeRanges,
  maxCps,
  minDurationMs,
  maxDurationMs,
  frameRate,
  issues,
  accepted,
  scrollToIndex,
  selectedIndex,
  activeIndex,
  onSelectCue,
}: CueTableProps) {
  // Roll the live QC issues up per cue (by 1-based Cue.index) for the inline
  // row markers; recompute only when the issue list identity changes.
  const rowIssues = useMemo(() => summarizeRowIssues(issues ?? []), [issues]);
  // Accepted issues grouped by cue → the rule ids the operator marked intentional
  // (cue-forge-n3e.11), so the metric tints can clear for the accepted cell.
  const acceptedRulesByCue = useMemo(() => {
    const m = new Map<number, Set<string>>();
    for (const a of accepted ?? []) {
      if (a.cueIndex < 0) continue;
      let s = m.get(a.cueIndex);
      if (!s) m.set(a.cueIndex, (s = new Set()));
      s.add(a.ruleId);
    }
    return m;
  }, [accepted]);
  const showActions = Boolean(
    onMergeNext || onSplit || onInsertAfter || onDelete || onAdvancedEdit,
  );
  // Truly empty (the whole document has no cues), not just filtered to zero rows
  // (cue-forge-wwy.2) — see totalCueCount's doc comment.
  const isEmptyDocument = (totalCueCount ?? cues.length) === 0;
  const showEmptyHint = isEmptyDocument && cues.length === 0 && Boolean(onInsertAfter);
  const colCount = showActions ? 7 : 6;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(DEFAULT_VIEWPORT);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [draft, setDraft] = useState('');
  /** Set when a timing commit failed to parse; keeps the editor open. */
  const [timeError, setTimeError] = useState<string | null>(null);
  /** Roving-tabindex target: the single cell reachable via Tab. */
  const [active, setActive] = useState<CellPos>({ row: 0, col: 0 });
  // Guards against a blur event re-committing after Enter already committed.
  const committedRef = useRef(false);
  /** Cell to focus after the next render (navigation / edit close). */
  const focusTargetRef = useRef<CellPos | null>(null);
  /**
   * Set when a keyboard-activated Merge/Delete removed the focused row
   * (cue-forge-d82): the row that held focus is gone and the browser drops
   * focus to <body>, so the roving target must be re-focused after the render
   * that re-clamps it. Only the grid's own action buttons set this, and only
   * on keyboard activation, so we never yank focus from an unrelated control.
   */
  const restoreFocusRef = useRef(false);
  const lastScrollToRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const h = scrollRef.current?.clientHeight ?? 0;
    if (h > 0) setViewport(h);
  }, []);

  // The single place that moves DOM focus, so two paths can never fight over it
  // (cue-forge-d82). It resolves one cell to focus per commit, in priority order:
  //   1. an explicit navigation target (arrow move, edit close, scrollToIndex) —
  //      always wins;
  //   2. otherwise a keyboard Merge/Delete that removed the focused row: pull
  //      focus back to the re-clamped roving cell so the operator stays in the
  //      grid instead of being dropped on <body>. Suppressed while an editor
  //      owns focus, and deferred when an external jump (scrollToIndex) is
  //      pending — the jump's own effect sets the target and focuses it next.
  useEffect(() => {
    let target = focusTargetRef.current;
    focusTargetRef.current = null;
    const restore = restoreFocusRef.current;
    restoreFocusRef.current = false;
    if (!target && restore && !editing && cues.length > 0) {
      const jumpPending = scrollToIndex != null && scrollToIndex !== lastScrollToRef.current;
      if (!jumpPending) {
        target = {
          row: Math.min(active.row, cues.length - 1),
          col: Math.min(active.col, colCount - 1),
        };
      }
    }
    if (!target) return;
    scrollRef.current
      ?.querySelector<HTMLElement>(`[data-cell="${target.row}:${target.col}"]`)
      ?.focus();
  });

  // External "jump to cue" requests carry the cue's `index` (1-based after
  // import renumbering). Resolve the row by that index, then scroll + focus AND
  // durably select it, so the located cue keeps its highlight after focus moves
  // on. `cues` may be a find-filtered subset of the document (cue-forge-q51), so
  // the target can legitimately be absent from the current view; when it is,
  // bail rather than clamping findIndex's -1 onto an unrelated row and durably
  // selecting the wrong cue (cue-forge-zxw.6).
  useEffect(() => {
    if (scrollToIndex == null) {
      lastScrollToRef.current = null;
      return;
    }
    if (scrollToIndex === lastScrollToRef.current || cues.length === 0) return;
    const row = cues.findIndex((c) => c.index === scrollToIndex);
    if (row < 0) return;
    lastScrollToRef.current = scrollToIndex;
    const top = scrollTopForRow(row, ROW_HEIGHT, cues.length, viewport);
    if (scrollRef.current) scrollRef.current.scrollTop = top;
    setScrollTop(top);
    setActive({ row, col: COL.text });
    focusTargetRef.current = { row, col: COL.text };
    onSelectCue?.(row);
  }, [scrollToIndex, cues, viewport, onSelectCue]);

  const { startIndex, endIndex, totalHeight } = virtualWindow(
    scrollTop,
    viewport,
    ROW_HEIGHT,
    cues.length,
  );
  // Pin the actively-edited row into the rendered set so the editor never
  // unmounts (losing the edit and stealing focus) when scrolled off-window.
  const editPos = editing ? cues.findIndex((c) => c.index === editing.cue.index) : -1;
  const entries = cues.slice(startIndex, endIndex).map((cue, i) => ({ cue, pos: startIndex + i }));
  if (editPos >= 0 && (editPos < startIndex || editPos >= endIndex)) {
    entries.push({ cue: cues[editPos]!, pos: editPos });
  }

  function beginEdit(cue: Cue, field: EditField) {
    committedRef.current = false;
    focusTargetRef.current = null; // the editor's autoFocus owns focus now
    setTimeError(null);
    setEditing({ cue, field });
    setDraft(
      field === 'text'
        ? cue.lines.map((l) => l.spans.map((s) => s.text).join('')).join('\n')
        : formatTimecode(field === 'start' ? cue.startMs : cue.endMs),
    );
  }

  /** Close the editor and hand focus back to the cell that was edited. */
  function closeEditor(cue: Cue, field: EditField) {
    setEditing(null);
    setTimeError(null);
    const pos = cues.findIndex((c) => c.index === cue.index);
    if (pos >= 0) {
      const cell = { row: pos, col: COL[field] };
      setActive(cell);
      focusTargetRef.current = cell;
    }
  }

  function cancelEdit() {
    if (!editing) return;
    committedRef.current = true;
    closeEditor(editing.cue, editing.field);
  }

  /** Commit `value` for the active edit; fires once even if blur races Enter. */
  function commit(value: string) {
    if (!editing || committedRef.current) return;
    const { cue, field } = editing;
    if (field === 'text') {
      committedRef.current = true;
      onCommitCue({ ...cue, lines: value.split('\n').map(textLine) });
    } else {
      const ms = parseTimecode(value);
      if (ms === null) {
        // Never silently drop an invalid timecode: keep the editor open with
        // a visible error; it closes only on a valid commit or Escape.
        setTimeError(TIMECODE_ERROR);
        return;
      }
      committedRef.current = true;
      onCommitCue(field === 'start' ? { ...cue, startMs: ms } : { ...cue, endMs: ms });
    }
    closeEditor(cue, field);
  }

  function onTimingKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit(e.currentTarget.value);
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  }

  function onTextKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      commit(e.currentTarget.value);
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  }

  /** Adjust the scroll offset so `row` is fully inside the viewport. */
  function ensureRowVisible(row: number) {
    const rowTop = row * ROW_HEIGHT;
    const rowBottom = rowTop + ROW_HEIGHT;
    let top = scrollTop;
    if (rowTop < top) top = rowTop;
    else if (rowBottom > top + viewport) top = rowBottom - viewport;
    if (top !== scrollTop) {
      if (scrollRef.current) scrollRef.current.scrollTop = top;
      setScrollTop(top);
    }
  }

  function moveActive(row: number, col: number) {
    if (cues.length === 0) return;
    const cell = {
      row: Math.max(0, Math.min(row, cues.length - 1)),
      col: Math.max(0, Math.min(col, colCount - 1)),
    };
    ensureRowVisible(cell.row);
    setActive(cell);
    focusTargetRef.current = cell;
  }

  /**
   * Whether the rendered cell at (row, col) can take focus. Below 700px the
   * Dur/CPS columns are `display: none` (CueTable.css) and focusing them would
   * silently drop keyboard focus, so horizontal moves skip them. Cells outside
   * the virtual window are not in the DOM; they count as visible (vertical
   * moves keep the column, and the focus effect runs after they render).
   */
  function isCellVisible(row: number, col: number): boolean {
    const el = scrollRef.current?.querySelector<HTMLElement>(`[data-cell="${row}:${col}"]`);
    return !el || getComputedStyle(el).display !== 'none';
  }

  function onCellKey(e: KeyboardEvent<HTMLElement>, pos: number, col: number, edit?: () => void) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveActive(pos + 1, col);
        return;
      case 'ArrowUp':
        e.preventDefault();
        moveActive(pos - 1, col);
        return;
      case 'ArrowRight':
        e.preventDefault();
        moveActive(
          pos,
          nextVisibleCol(col, 1, colCount, (c) => isCellVisible(pos, c)),
        );
        return;
      case 'ArrowLeft':
        e.preventDefault();
        moveActive(
          pos,
          nextVisibleCol(col, -1, colCount, (c) => isCellVisible(pos, c)),
        );
        return;
    }
    if (edit && (e.key === 'Enter' || e.key === ' ' || e.key === 'F2')) {
      e.preventDefault();
      edit();
    }
  }

  /** Roving tabindex + arrow navigation, shared by every display gridcell. */
  function cellNavProps(pos: number, col: number, edit?: () => void) {
    const isActive = activeCell.row === pos && activeCell.col === col;
    return {
      'data-cell': `${pos}:${col}`,
      tabIndex: isActive ? 0 : -1,
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => onCellKey(e, pos, col, edit),
      onFocus: () => {
        if (!isActive) setActive({ row: pos, col });
      },
    };
  }

  const isEditing = (cue: Cue, field: EditField) =>
    editing?.cue.index === cue.index && editing.field === field;

  // `active` can go stale after a structural shrink (merge/delete —
  // cue-forge-49u): it's state private to this component, so a parent that
  // removes cues has no way to keep it in bounds. Re-derive the in-bounds
  // cell at render time (cheap, and simpler than an effect that mirrors it
  // back into state) rather than mutating `active` itself — a later click or
  // Tab still resolves against the real, unclamped value via cellNavProps'
  // onFocus. Empty table → the initial-state cell, which matches nothing
  // rendered, so no cell claims tabIndex 0.
  const activeCell: CellPos =
    cues.length === 0
      ? { row: 0, col: 0 }
      : {
          row: Math.min(active.row, cues.length - 1),
          col: Math.min(active.col, colCount - 1),
        };

  return (
    <div
      className={`cf-cuetable${showActions ? ' cf-cuetable--actions' : ''}`}
      role="grid"
      aria-rowcount={cues.length + 1}
      aria-colcount={colCount}
      aria-label="Cues"
    >
      <div className="cf-cuetable__head" role="row" aria-rowindex={1}>
        <span role="columnheader" className="cf-col-index">
          #
        </span>
        <span role="columnheader" className="cf-col-time">
          Start
        </span>
        <span role="columnheader" className="cf-col-time">
          End
        </span>
        <span role="columnheader" className="cf-col-num">
          Dur
        </span>
        <span role="columnheader" className="cf-col-num">
          CPS
        </span>
        <span role="columnheader" className="cf-col-text">
          Text
        </span>
        {showActions && (
          <span role="columnheader" className="cf-col-actions">
            Actions
          </span>
        )}
      </div>

      <div
        className="cf-cuetable__scroll"
        ref={scrollRef}
        onScroll={(e) => {
          setScrollTop(e.currentTarget.scrollTop);
          if (e.currentTarget.clientHeight > 0) setViewport(e.currentTarget.clientHeight);
        }}
      >
        {showEmptyHint && (
          <div className="cf-cuetable__empty">
            <p className="cf-cuetable__empty-text">No cues yet.</p>
            <button
              type="button"
              className="cf-btn"
              aria-label="Add first cue"
              onClick={() => onInsertAfter!(-1)}
            >
              Add first cue
            </button>
          </div>
        )}
        <div className="cf-cuetable__spacer" style={{ height: totalHeight }}>
          {entries.map(({ cue, pos }) => {
            const top = pos * ROW_HEIGHT;
            const cps = cueCps(cue, cpsExcludeRanges);
            const durMs = durationMs(cue);
            // Suppress the metric tint on a cell whose issue the operator accepted
            // (cue-forge-n3e.11) so an accepted cue stops reading as still-wrong.
            const accRules = acceptedRulesByCue.get(cue.index);
            const cpsAccepted = accRules?.has('max-cps') ?? false;
            const durAccepted =
              (accRules?.has('min-duration') || accRules?.has('max-duration')) ?? false;
            const cpsState = cpsAccepted ? null : cpsThreshold(cps, maxCps);
            const durState = durAccepted
              ? null
              : durationThreshold(durMs, minDurationMs, maxDurationMs);
            // Frame-aware duration (cue-forge-d9b.1): frames when the doc declares
            // a frame rate, else seconds; the dropped unit is kept in the title.
            const durSeconds = `${(durMs / 1000).toFixed(2)}s`;
            const durThresholdTitle = durAccepted
              ? undefined
              : durationTitle(durMs, minDurationMs, maxDurationMs);
            const durText =
              frameRate !== undefined ? `${durationFrames(durMs, frameRate)} fr` : durSeconds;
            const durCellTitle =
              frameRate !== undefined
                ? [durSeconds, durThresholdTitle].filter(Boolean).join(' — ')
                : durThresholdTitle;
            // QC flags for this row (cue-forge-n3e.5): the gutter marker shows the
            // worst severity; flagCell tints the offending start/end/text cell.
            const flags = rowIssues.get(cue.index);
            const flagCell = (col: FlagColumn) =>
              flags?.cells[col] ? ` cf-cell--flag-${flags.cells[col]}` : '';
            // The original source caption number, shown only when repair renumbered
            // it away from the canonical index (cue-forge-q7q), so an operator can
            // reconcile our #N with the delivery file's numbering. Surfaced via the
            // index cell's title + SR text rather than a visible column, to avoid
            // widening the fixed-width virtualized gutter.
            const sourceLabel =
              cue.sourceIndex !== undefined ? `Source #${cue.sourceIndex}` : undefined;
            const indexTitle =
              [flags ? rowFlagLabel(flags) : null, sourceLabel].filter(Boolean).join(' · ') ||
              undefined;
            return (
              <div
                key={cue.index}
                className={`cf-cuetable__row${
                  pos === selectedIndex ? ' cf-cuetable__row--selected' : ''
                }${pos === activeIndex ? ' cf-cuetable__row--active' : ''}`}
                role="row"
                // ARIA row index must be consistent with aria-rowcount, which
                // counts the header plus the currently-shown (possibly filtered)
                // `cues` set — not the whole document. So the row's position is
                // its 0-based slot in that visible set (`pos`), +1 for 1-based
                // and +1 for the header row. Using the canonical `cue.index`
                // here would announce e.g. "row 501 of 4" once a find-filter
                // narrows 10k cues to 3 (cue-forge-w28.16). The two coincide
                // for an unfiltered grid, where cues[pos].index === pos + 1.
                aria-rowindex={pos + 2}
                aria-selected={pos === selectedIndex || undefined}
                onClick={onSelectCue ? () => onSelectCue(pos) : undefined}
                style={{ top, height: ROW_HEIGHT }}
              >
                <span
                  role="gridcell"
                  className="cf-col-index cf-mono"
                  title={indexTitle}
                  {...cellNavProps(pos, COL.index)}
                >
                  {/* Fixed-width marker slot keeps the numbers aligned whether
                      or not a row is flagged; the shape (filled disc = error,
                      hollow ring = warning) mirrors the Inspector so severity
                      survives grayscale (WCAG 1.4.1). */}
                  <span
                    className={`cf-row-flag${flags ? ` cf-row-flag--${flags.severity}` : ''}`}
                    aria-hidden="true"
                  />
                  {flags && <span className="cf-visually-hidden">{rowFlagLabel(flags)}</span>}
                  {sourceLabel && <span className="cf-visually-hidden">{sourceLabel}</span>}
                  <span className="cf-row-num">{cue.index}</span>
                </span>

                {isEditing(cue, 'start') ? (
                  <span role="gridcell" className="cf-col-time cf-cell-editor">
                    <input
                      className="cf-mono cf-cell-input"
                      aria-label="Start"
                      aria-invalid={timeError ? true : undefined}
                      aria-describedby={timeError ? 'cf-timecode-error' : undefined}
                      dir="ltr"
                      autoFocus
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={onTimingKey}
                      onBlur={(e) => commit(e.currentTarget.value)}
                    />
                  </span>
                ) : (
                  <span
                    role="gridcell"
                    className={`cf-col-time cf-mono cf-cell${flagCell('start')}`}
                    dir="ltr"
                    onClick={() => beginEdit(cue, 'start')}
                    {...cellNavProps(pos, COL.start, () => beginEdit(cue, 'start'))}
                  >
                    {formatTimecode(cue.startMs)}
                  </span>
                )}

                {isEditing(cue, 'end') ? (
                  <span role="gridcell" className="cf-col-time cf-cell-editor">
                    <input
                      className="cf-mono cf-cell-input"
                      aria-label="End"
                      aria-invalid={timeError ? true : undefined}
                      aria-describedby={timeError ? 'cf-timecode-error' : undefined}
                      dir="ltr"
                      autoFocus
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={onTimingKey}
                      onBlur={(e) => commit(e.currentTarget.value)}
                    />
                  </span>
                ) : (
                  <span
                    role="gridcell"
                    className={`cf-col-time cf-mono cf-cell${flagCell('end')}`}
                    dir="ltr"
                    onClick={() => beginEdit(cue, 'end')}
                    {...cellNavProps(pos, COL.end, () => beginEdit(cue, 'end'))}
                  >
                    {formatTimecode(cue.endMs)}
                  </span>
                )}

                <span
                  role="gridcell"
                  className={`cf-col-num cf-mono${durState ? ` cf-col-num--${durState}` : ''}`}
                  dir="ltr"
                  title={durCellTitle}
                  {...cellNavProps(pos, COL.dur)}
                >
                  {durText}
                </span>
                <span
                  role="gridcell"
                  className={`cf-col-num cf-mono${cpsState ? ` cf-col-num--${cpsState}` : ''}`}
                  dir="ltr"
                  title={
                    cpsState
                      ? cpsState === 'error'
                        ? `Over the reading-speed limit (${maxCps})`
                        : `Near the reading-speed limit (${maxCps})`
                      : undefined
                  }
                  {...cellNavProps(pos, COL.cps)}
                >
                  {cps.toFixed(1)}
                </span>

                {isEditing(cue, 'text') ? (
                  <span role="gridcell" className="cf-col-text cf-cell-editor">
                    <textarea
                      className="cf-mono cf-cell-input"
                      aria-label="Text"
                      aria-keyshortcuts="Control+Enter Escape"
                      dir="auto"
                      autoFocus
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={onTextKey}
                      onBlur={(e) => commit(e.currentTarget.value)}
                    />
                  </span>
                ) : (
                  <span
                    role="gridcell"
                    className={`cf-col-text cf-cell cf-cell-text${flagCell('text')}`}
                    onClick={() => beginEdit(cue, 'text')}
                    {...cellNavProps(pos, COL.text, () => beginEdit(cue, 'text'))}
                  >
                    {cue.lines.map((l, li) => (
                      // One block per line so dir="auto" resolves per line —
                      // a Latin first line must not force LTR on an Arabic one.
                      <span key={li} className="cf-cell-line" dir="auto">
                        {l.spans.map((s) => s.text).join('')}
                      </span>
                    ))}
                  </span>
                )}

                {showActions && (
                  <span
                    role="gridcell"
                    className="cf-col-actions cf-row-actions"
                    {...cellNavProps(pos, COL.actions)}
                  >
                    {onMergeNext && (
                      <button
                        type="button"
                        className="cf-row-action"
                        aria-label={`Merge cue ${cue.index} with next`}
                        disabled={mergeSplitDisabled || pos >= cues.length - 1}
                        // A disabled control must say why it is disabled
                        // (cue-forge-drt.15): the last cue has no following cue
                        // to merge into, distinct from the filter-active case.
                        title={
                          mergeSplitDisabled
                            ? 'Clear the filter to merge or split'
                            : pos >= cues.length - 1
                              ? 'Nothing after this cue to merge with'
                              : undefined
                        }
                        // Don't let the row's select handler fire too (cue-forge-9ww.7):
                        // a structural action must not yank the durable selection.
                        onClick={(e) => {
                          e.stopPropagation();
                          // Keyboard activation reports detail 0 (mouse ≥1); the
                          // focused row vanishes on merge, so flag a roving-target
                          // focus restore (cue-forge-d82).
                          if (e.detail === 0) restoreFocusRef.current = true;
                          onMergeNext(pos);
                        }}
                      >
                        Merge
                      </button>
                    )}
                    {onSplit && (
                      <button
                        type="button"
                        className="cf-row-action"
                        aria-label={`Split cue ${cue.index}`}
                        disabled={mergeSplitDisabled}
                        title={
                          mergeSplitDisabled ? 'Clear the filter to merge or split' : undefined
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          onSplit(pos);
                        }}
                      >
                        Split
                      </button>
                    )}
                    {onAdvancedEdit && (
                      <button
                        type="button"
                        className="cf-row-action"
                        aria-label={`Advanced edit cue ${cue.index}`}
                        disabled={advancedEditDisabled}
                        title={
                          advancedEditDisabled ? 'This format carries no inline styling' : undefined
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          onAdvancedEdit(cue);
                        }}
                      >
                        Advanced
                      </button>
                    )}
                    {onInsertAfter && (
                      <button
                        type="button"
                        className="cf-row-action cf-row-action--icon"
                        aria-label={`Insert cue after ${cue.index}`}
                        disabled={mergeSplitDisabled}
                        title={
                          mergeSplitDisabled
                            ? 'Clear the filter to insert or delete'
                            : 'Insert cue below'
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          onInsertAfter(pos);
                        }}
                      >
                        +
                      </button>
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        className="cf-row-action cf-row-action--icon"
                        aria-label={`Delete cue ${cue.index}`}
                        disabled={mergeSplitDisabled}
                        title={
                          mergeSplitDisabled ? 'Clear the filter to insert or delete' : 'Delete cue'
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          // Keyboard activation reports detail 0 (mouse ≥1); the
                          // focused row vanishes on delete, so flag a roving-target
                          // focus restore (cue-forge-d82).
                          if (e.detail === 0) restoreFocusRef.current = true;
                          onDelete(pos);
                        }}
                      >
                        ×
                      </button>
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {editing && (
        <div className="cf-cuetable__status">
          {timeError ? (
            <span id="cf-timecode-error" className="cf-cuetable__status--error" role="alert">
              {timeError}
            </span>
          ) : (
            <span className="cf-cuetable__status--hint" aria-hidden="true">
              {editing.field === 'text'
                ? 'Ctrl+Enter to save · Esc to cancel'
                : 'Enter to save · Esc to cancel'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
